import { useMemo } from 'react';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import { ThinkingEffort } from '../../../types/backend';
import type {
  AgentSpec, AgentSpecUpdateRequest, ThinkingEffortType,
  AvailableModel, EffectiveLoadout, ToolLoadout,
  AgentReferences, CreateAgentRequest, DefaultAgents, SetDefaultAgentRequest, InvocationShapeInfo, InvocationShapeType,
  RetireAgentResult, RunnableAgent,
} from '../../../types/backend';

// Wire payload for a save. The generated AgentSpecUpdateRequest collapses the
// backend's Optional[...] tunables to `field?: T`, dropping the null arm — but an
// explicit null is exactly how the backend clears a tunable (exclude_unset). So the
// two clearable fields are widened to accept null here.
export type AgentSpecUpdatePayload = Omit<
  AgentSpecUpdateRequest, 'thinking_effort' | 'context_window_size' | 'tool_loadout'
> & {
  thinking_effort: string | null;
  context_window_size: number | null;
  // The backend requires a COMPLETE loadout object on every write (it SETs the whole
  // attribute, so an omitted key would silently reset it), or an explicit null to clear it
  // and go back to the baseline. The generated type marks all three keys optional, which
  // would let a partial write through, so the complete shape is spelled out here.
  tool_loadout?: Required<Pick<ToolLoadout, 'tools' | 'blocks' | 'avp_visibility_filter'>> | null;
};

// The effort tiers offered by the editor select, sourced from the generated enum
// so the option set can never drift from the backend ThinkingEffort definition.
export const THINKING_EFFORT_TIERS = Object.values(ThinkingEffort) as ThinkingEffortType[];

export const createAgentSpecService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    listAgentSpecs: async (): Promise<AgentSpec[]> =>
      rpcCall<AgentSpec[]>(client, 'agentspec.ListAgentSpecs'),

    getAgentSpec: async (agentId: AgentSpec['agent_id']): Promise<AgentSpec> =>
      rpcCall(client, 'agentspec.GetAgentSpec', { agent_id: agentId }),

    // Which models this environment can actually run, and the ones it cannot with the reason.
    listAvailableModels: async (): Promise<AvailableModel[]> =>
      rpcCall<AvailableModel[]>(client, 'agentspec.ListAvailableModels'),

    // The toolbox an agent runs now: its stored loadout, the baseline in code, or neither.
    getEffectiveLoadout: async (agentId: AgentSpec['agent_id']): Promise<EffectiveLoadout> =>
      rpcCall(client, 'agentspec.GetEffectiveLoadout', { agent_id: agentId }),

    updateAgentSpec: async (request: AgentSpecUpdatePayload): Promise<AgentSpec> =>
      rpcCall(client, 'agentspec.UpdateAgentSpec', request as unknown as Record<string, unknown>),

    // The three shapes, what each decides and the tools an agent of it may hold (ADR-0064).
    listInvocationShapes: async (): Promise<InvocationShapeInfo[]> =>
      rpcCall<InvocationShapeInfo[]>(client, 'agentspec.ListInvocationShapes'),

    // The active agents of one shape, as a picker offers them.
    listRunnableAgents: async (shape: InvocationShapeType): Promise<RunnableAgent[]> =>
      rpcCall(client, 'agentspec.ListRunnableAgents', { shape }),

    createAgent: async (request: CreateAgentRequest): Promise<AgentSpec> =>
      rpcCall(client, 'agentspec.CreateAgent', request),

    retireAgent: async (agentId: AgentSpec['agent_id']): Promise<RetireAgentResult> =>
      rpcCall(client, 'agentspec.RetireAgent', { agent_id: agentId }),

    // The investigation agent and the automation agent: what a new case, or a rule saved
    // without an agent, runs.
    getDefaultAgents: async (): Promise<DefaultAgents> =>
      rpcCall<DefaultAgents>(client, 'agentspec.GetDefaultAgents'),

    setDefaultAgent: async (request: SetDefaultAgentRequest): Promise<DefaultAgents> =>
      rpcCall(client, 'agentspec.SetDefaultAgent', request),

    // The rules and templates that still name an agent, so retiring it can say what will fail.
    listRulesNamingAgent: async (agentId: AgentSpec['agent_id']): Promise<AgentReferences> =>
      rpcCall(client, 'events.ListRulesNamingAgent', { agent_id: agentId }),
  };
};

export const useAgentSpecService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('useAgentSpecService requires authentication');
    }

    return createAgentSpecService(idToken);
  }, [idToken]);
};
